import { useMemo, type ReactElement } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { fetchTransactions, type TransactionRow } from '../../api/admin.js';
import { useUrlParam } from '../../lib/useUrlTab.js';
import { nanoToUsd, nanoToUsdCompact, formatDurationMs, formatDateTime, shortId } from '../../lib/format.js';
import type { CsvColumn } from '../../lib/csv.js';
import { ReportFilters, useReportFilters } from './components/ReportFilters.js';
import { ExportCsvButton } from './components/ExportCsvButton.js';

const CSV_COLUMNS: CsvColumn<TransactionRow>[] = [
  { header: 'started_at', value: (r) => r.startedAt },
  { header: 'operation', value: (r) => r.operation },
  { header: 'kind', value: (r) => r.kind },
  { header: 'model', value: (r) => r.model },
  { header: 'provider', value: (r) => r.provider },
  { header: 'cost_usd', value: (r) => nanoToUsd(r.costNano) },
  { header: 'duration_ms', value: (r) => r.durationMs ?? '' },
  { header: 'is_error', value: (r) => (r.isError ? 'true' : 'false') },
  { header: 'user_email', value: (r) => r.userEmail },
  { header: 'session_id', value: (r) => r.sessionId },
  { header: 'span_id', value: (r) => r.spanId },
];

export function TransactionsPage(): ReactElement {
  const filters = useReportFilters();
  const [operation, setOperation] = useUrlParam('operation');

  const query = useInfiniteQuery({
    queryKey: ['admin', 'reports', 'transactions', { f: filters.key, operation }],
    queryFn: ({ pageParam }) =>
      fetchTransactions({ filters: filters.toParams(), operation, cursor: pageParam, limit: 100 }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? null,
  });

  const rows = useMemo(
    () => (query.data?.pages ?? []).flatMap((p) => p.items),
    [query.data],
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Transacciones</h1>
          <p className="page-sub">
            Detalle tx-a-tx, más reciente primero ·{' '}
            <span className="mono">
              {filters.from} → {filters.to}
            </span>
            {operation ? (
              <>
                {' '}
                · operación{' '}
                <button type="button" className="chip is-on" onClick={() => setOperation(null)}>
                  {operation} ✕
                </button>
              </>
            ) : null}
          </p>
        </div>
        <ExportCsvButton filename="transactions" rows={rows} columns={CSV_COLUMNS} />
      </div>

      <ReportFilters state={filters} />

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <div className="pad-7">
          <section className="card">
            <table className="table" data-testid="transactions-table">
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>Operación</th>
                  <th>Kind</th>
                  <th>Modelo</th>
                  <th className="r">Costo</th>
                  <th className="r">Dur.</th>
                  <th>User</th>
                  <th>Sesión</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="muted" style={{ textAlign: 'center' }}>
                      Sin transacciones en el rango / filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.spanId} className={r.isError ? 'is-error' : undefined}>
                      <td className="mono tt-sec" title={r.startedAt}>
                        {formatDateTime(r.startedAt)}
                      </td>
                      <td className="mono tt-sec">
                        {r.operation ?? '—'}
                        {r.isError ? <span className="badge b-error" style={{ marginLeft: 6 }}>error</span> : null}
                      </td>
                      <td className="mono tt-sec">
                        <span className="badge">{r.kind}</span>
                      </td>
                      <td className="mono tt-sec">
                        {r.model ?? <span className="muted">—</span>}
                      </td>
                      <td className="r mono">{nanoToUsdCompact(r.costNano)}</td>
                      <td className="r mono tt-sec">{formatDurationMs(r.durationMs)}</td>
                      <td className="mono tt-sec">{r.userEmail ?? <span className="muted">—</span>}</td>
                      <td className="mono tt-sec">
                        {r.sessionId ? (
                          <Link to={`/sessions/${r.sessionId}`}>{shortId(r.sessionId)}</Link>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div style={{ padding: 'var(--s-5)', textAlign: 'center' }}>
              {query.hasNextPage ? (
                <button
                  type="button"
                  className="chip"
                  onClick={() => query.fetchNextPage()}
                  disabled={query.isFetchingNextPage}
                  data-testid="load-more"
                >
                  {query.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
                </button>
              ) : rows.length > 0 ? (
                <span className="muted">— fin ({rows.length} transacciones) —</span>
              ) : null}
            </div>
          </section>
        </div>
      )}
    </>
  );
}
